import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { graphql } from '@e-socna/data-layer';
import type { ActorUnionInput, Id } from '@e-socna/data-layer';
import type { ResultOf } from '@graphql-typed-document-node/core';

import { generateBasicQueryOptions } from '../../../../../hooks/useGraphQLBasicQuery.hook';

export const FolderBusinessEntityForm_FolderActorListsChangeFreezes_QueryDocument =
  graphql(`
    query FolderBusinessEntityForm_FolderActorListsChangeFreezes_Query(
      $id: Id!
      $internalActors: [ActorUnionInput!]
      $externalActors: [ActorUnionInput!]
    ) {
      findOneFolder(id: $id) {
        _id
        actorListsChangeFreezes(
          internalActors: $internalActors
          externalActors: $externalActors
        ) {
          level
          lists
          refusal
          missionId
          missionKind
          compositeMissionId
          businessEventId
        }
      }
    }
  `);

export const FolderBusinessEntityForm_MissionActorListsChangeFreezes_QueryDocument =
  graphql(`
    query FolderBusinessEntityForm_MissionActorListsChangeFreezes_Query(
      $id: Id!
      $internalActors: [ActorUnionInput!]
      $externalActors: [ActorUnionInput!]
    ) {
      findOneMission(id: $id) {
        _id
        actorListsChangeFreezes(
          internalActors: $internalActors
          externalActors: $externalActors
        ) {
          level
          lists
          refusal
          missionId
          missionKind
          compositeMissionId
          businessEventId
        }
      }
    }
  `);

export type ActorListsFreeze = ResultOf<
  typeof FolderBusinessEntityForm_FolderActorListsChangeFreezes_QueryDocument
>['findOneFolder']['actorListsChangeFreezes'][number];

// The lists an actorable is about to get: an absent list is inherited.
export interface ProposedActorLists {
  readonly internalActors: readonly ActorUnionInput[] | null;
  readonly externalActors: readonly ActorUnionInput[] | null;
}

export type PreviewFreezes = (
  lists: ProposedActorLists,
) => Promise<readonly ActorListsFreeze[]>;

// The verdicts on the levels inheriting the lists of an existing folder or mission that a change
// of its lists reaches (kept of their own, or refusing the change), asked to the back as it
// computes them when writing.
export const useActorListsChangeFreezes = (
  level: 'folder' | 'mission',
  id: Id,
): PreviewFreezes => {
  const queryClient = useQueryClient();

  return useCallback(
    async ({ internalActors, externalActors }) => {
      const variables = {
        id,
        internalActors: internalActors as ActorUnionInput[] | null,
        externalActors: externalActors as ActorUnionInput[] | null,
      };

      if (level === 'folder') {
        const { findOneFolder } = await queryClient.query(
          generateBasicQueryOptions({
            document:
              FolderBusinessEntityForm_FolderActorListsChangeFreezes_QueryDocument,
            variables,
            useQueryOptions: { staleTime: 0, gcTime: 0 },
          }),
        );

        return findOneFolder.actorListsChangeFreezes;
      }

      const { findOneMission } = await queryClient.query(
        generateBasicQueryOptions({
          document:
            FolderBusinessEntityForm_MissionActorListsChangeFreezes_QueryDocument,
          variables,
          useQueryOptions: { staleTime: 0, gcTime: 0 },
        }),
      );

      return findOneMission.actorListsChangeFreezes;
    },
    [id, level, queryClient],
  );
};
